import React, { useState, useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import Button, { ButtonType } from 'components/Button';
import { BankAccountInfo, SavingsAccount } from 'api/PlaidApi';
import { updateApplicationCheckingAccount } from 'thunks';
import useDispatchWithUnwrap from 'hooks/useDispatchWithUnwrap';
import { setActiveWorkflow, WorkflowTypes } from 'handlers/aiChat';
import { getApplicationData } from 'selectors/getApplicationData';
import { getBankAccount } from 'selectors/getBankAccount';
import { getAIChat } from 'selectors/aiChat';

import AccountRow from './AccountRow';

import styles from './BankAccountsList.module.scss';

export enum BankAccountType {
  Checking = 'checking',
  Savings = 'savings',
}

type AccountType = BankAccountInfo | SavingsAccount;

interface BankAccountsListProps {
  onContinue: (selectedAccount: BankAccountInfo | SavingsAccount) => void | Promise<void>;
  buttonText?: string;
  accountType?: BankAccountType;
  isLoading?: boolean;
}

const BankAccountsList = ({
  onContinue,
  buttonText = 'Continue',
  accountType = BankAccountType.Checking,
  isLoading = false,
}: BankAccountsListProps) => {
  const dispatch = useDispatch();
  const dispatchWithUnwrap = useDispatchWithUnwrap();
  const { bankAccounts: checkingAccounts, savingsAccounts } = useSelector(getBankAccount);
  const { application } = useSelector(getApplicationData);
  const { isOpen: isAIChatOpen } = useSelector(getAIChat);

  const isSavings = accountType === BankAccountType.Savings;
  const accounts = isSavings ? savingsAccounts : checkingAccounts;

  const [selectedAccount, setSelectedAccount] = useState<string>(accounts.length > 0 ? accounts[0].id : '');
  const [isUpdating, setIsUpdating] = useState(false);

  useEffect(() => {
    if (accounts.length > 0 && !selectedAccount) {
      setSelectedAccount(accounts[0].id);
    }
  }, [accounts, selectedAccount]);

  const handleAccountChange = (account: AccountType) => {
    if (account.id === selectedAccount) {
      return;
    }

    setSelectedAccount(account.id);
  };

  const handleContinue = useCallback(async () => {
    if (!selectedAccount) return;

    const account = accounts.find((acc) => acc.id === selectedAccount);
    if (!account) return;

    try {
      setIsUpdating(true);

      // Only update checking account if it's a checking account
      if (!isSavings) {
        await dispatchWithUnwrap(
          updateApplicationCheckingAccount({
            applicationId: application!.id,
            bankAccountId: selectedAccount,
          }),
        );
      }

      // For savings accounts, just call onContinue directly
      await onContinue(account as BankAccountInfo | SavingsAccount);
    } finally {
      setIsUpdating(false);
    }
  }, [selectedAccount, application, dispatchWithUnwrap, onContinue, accounts, isSavings]);

  const handleAddAnotherAccount = () => {
    // TODO: loader
    dispatch(
      setActiveWorkflow({
        success: false,
        type: WorkflowTypes.Plaid,
        isLoading: true,
        openPlaid: true,
        fullScreen: true,
      }),
    );
  };

  return (
    <>
      <div>
        {accounts.map((account, index) => {
          const accountName = isSavings
            ? (account as SavingsAccount).name
            : (account as BankAccountInfo).officialName || (account as BankAccountInfo).name;
          const { mask } = account;
          const balance = isSavings ? undefined : (account as BankAccountInfo).balance;

          return (
            <AccountRow
              key={`${account.id}-${index}`}
              selected={selectedAccount === account.id}
              onClick={() => handleAccountChange(account)}
              accountName={accountName}
              accountNumber={mask}
              accountBalance={balance}
            />
          );
        })}
      </div>
      <div className={styles.buttonsContainer}>
        <Button onClick={handleContinue} disabled={!selectedAccount} isLoading={isLoading || isUpdating}>
          {buttonText}
        </Button>
        {isAIChatOpen && (
          <Button onClick={handleAddAnotherAccount} type={ButtonType.Transparent} disabled={isLoading || isUpdating}>
            Add another account
          </Button>
        )}
      </div>
    </>
  );
};

export default BankAccountsList;
